import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { AuthActionError, signInWithOAuth, type OAuthProvider } from '@/lib/auth/actions';

interface ProviderStyle {
  id: OAuthProvider;
  icon: keyof typeof Ionicons.glyphMap;
  bg: string;
  fg: string;
  border?: string;
  labelKey: string;
  name: string;
}

const PROVIDERS: ProviderStyle[] = [
  {
    id: 'google',
    icon: 'logo-google',
    bg: '#FFFFFF',
    fg: '#1A1A1A',
    labelKey: 'auth.continueWithGoogle',
    name: 'Google',
  },
  {
    id: 'facebook',
    icon: 'logo-facebook',
    bg: '#1877F2',
    fg: '#FFFFFF',
    labelKey: 'auth.continueWithFacebook',
    name: 'Facebook',
  },
];

/**
 * The competitor-style provider stack (Google / Facebook), branded and
 * full-width. Enabled providers sign in; ones not yet configured in Supabase
 * surface a friendly "coming soon" via onError instead of failing silently.
 */
export function SocialAuthButtons({
  onError,
  onSuccess,
}: {
  onError: (message: string) => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<OAuthProvider | null>(null);

  const handlePress = (provider: ProviderStyle) => {
    setBusy(provider.id);
    signInWithOAuth(provider.id)
      .then(() => {
        // Web redirects away before this resolves; native resolves here.
        setBusy(null);
        onSuccess();
      })
      .catch((error) => {
        setBusy(null);
        if (error instanceof AuthActionError && error.translationKey === 'errors.OAUTH_DISABLED') {
          onError(t('auth.providerComingSoon', { provider: provider.name }));
        } else {
          onError(
            error instanceof AuthActionError ? t(error.translationKey) : t('auth.errors.generic'),
          );
        }
      });
  };

  return (
    <View className="gap-2.5">
      {PROVIDERS.map((provider) => (
        <Pressable
          key={provider.id}
          accessibilityRole="button"
          accessibilityLabel={t(provider.labelKey)}
          disabled={busy !== null}
          onPress={() => handlePress(provider)}
          className="h-12 flex-row items-center justify-center gap-3 rounded-2xl px-5 active:opacity-80"
          style={{
            backgroundColor: provider.bg,
            borderWidth: provider.border ? 1 : 0,
            borderColor: provider.border,
            opacity: busy !== null && busy !== provider.id ? 0.5 : 1,
          }}
        >
          {busy === provider.id ? (
            <ActivityIndicator size="small" color={provider.fg} />
          ) : (
            <Ionicons name={provider.icon} size={19} color={provider.fg} />
          )}
          <AppText variant="body" weight="semibold" style={{ color: provider.fg }}>
            {t(provider.labelKey)}
          </AppText>
        </Pressable>
      ))}
    </View>
  );
}
